import React, { useState, useEffect } from "react";
import { db, functions } from "../firebase/fire";
import { collection, getDocs, updateDoc, doc, addDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import LoadingOverlay from "./LoadingOverlay";
import Modal from "./Modal";
import { FaUndo, FaTrash } from "react-icons/fa";

const deleteMemberAccount = httpsCallable(functions, "deleteMemberAccount");

export default function ArchivedMembersPage({ theme = 'light' }) {
    const [archivedMembers, setArchivedMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const membersPerPage = 7;

    const showModal = (message) => {
        setModalMessage(message);
        setModalOpen(true);
    };


    useEffect(() => {
        const fetchArchivedMembers = async () => {
            setIsLoading(true);
            const snapshot = await getDocs(collection(db, "members"));
            const data = snapshot.docs
                .map((doc) => {
                    const d = doc.data();
                    return d ? { id: doc.id, ...d } : null;
                })
                .filter((m) => m && m.archived);
            setArchivedMembers(data);
            setIsLoading(false);
        };
        fetchArchivedMembers();
    }, []);

    const logMemberChange = async (memberId, changesObj, actionType) => {
        const changeEntries = Object.entries(changesObj).map(([field, { before, after }]) => ({
            field,
            before,
            after
        }));
        await addDoc(collection(db, "memberAuditLog"), {
            memberId,
            changes: changeEntries,
            action: actionType,
            timestamp: new Date()
        });
    };
    const handleUnarchive = async (memberId) => {
        setIsLoading(true);
        try {
            await updateDoc(doc(db, "members", memberId), { archived: false });
            const prevMember = currentMembers.find(m => m.id === memberId);
            await logMemberChange(
                memberId,
                { archived: { before: prevMember?.archived ?? false, after: true } },
                "Restore from Archive"
            );
            setArchivedMembers((prev) => prev.filter((m) => m.id !== memberId));
            showModal("Member restored successfully");
        } catch (error) {
            console.error("Error restoring archived member:", error);
            showModal("Failed to restore member. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteFromArchive = async (memberId) => {
        const memberToDelete = archivedMembers.find((member) => member.id === memberId);
        if (!memberToDelete) return;

        const memberName = memberToDelete.name || "this member";
        if (!window.confirm(`Are you sure you want to permanently delete ${memberName}? This removes them from the database and authentication.`)) {
            return;
        }

        setIsLoading(true);
        try {
            const uid = memberToDelete.uid || memberId;
            const result = await deleteMemberAccount({ uid, memberId });

            if (result?.data?.success) {
                await logMemberChange(
                    memberId,
                    {
                        deleted: {
                            before: true,
                            after: false
                        }
                    },
                    "Permanent Delete from Archive"
                );
                setArchivedMembers((prev) => prev.filter((member) => member.id !== memberId));
                showModal("Member deleted permanently from the database and authentication.");
            } else {
                showModal("Member delete request did not complete successfully.");
            }
        } catch (error) {
            console.error("Error deleting archived member:", error);
            showModal(error?.message || "Failed to delete member. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredMembers = archivedMembers.filter(
        member =>
        (member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Pagination logic
    const indexOfLast = currentPage * membersPerPage;
    const indexOfFirst = indexOfLast - membersPerPage;
    const currentMembers = filteredMembers.slice(indexOfFirst, indexOfLast);

    return (
        <LoadingOverlay isLoading={isLoading}>
            <div className={`container mx-auto p-4 ${
                theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
            }`}>
                {/* <h1 className="text-2xl font-bold mb-4">Archived Members</h1> */}
                <input
                    type="text"
                    placeholder="Search members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full p-3 mb-4 border-2 rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                            : 'bg-white border-blue-500 text-gray-900 focus:ring-blue-300'
                    }`}
                />
                <table className="w-full text-m text-center">
                    <thead className={theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-blue-600 text-white'}>
                        <tr>
                            <th>Photo</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Membership</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Barcode</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentMembers.map((member) => (
                            <tr key={member.id} className={theme === 'dark' ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                                <td>{member.photo ? (
                                    <img src={member.photo} alt="Member" className="mx-auto rounded-full h-14 w-14 object-cover mb-4" />
                                ) : (
                                    <div className="mx-auto rounded-full h-14 w-14 bg-gray-200 flex items-center justify-center mb-4">
                                        <span className="text-gray-500">No Photo</span>
                                    </div>
                                )}</td>
                                <td>{member.name}</td>

                                <td>{member.email}</td>
                                <td>{member.phone}</td>
                                <td>{member.status}</td>
                                <td>
                                    {member.membershipStartDate
                                        ? member.membershipStartDate.toDate().toLocaleDateString()
                                        : "N/A"}
                                </td>
                                <td>
                                    {member.membershipEndDate
                                        ? member.membershipEndDate.toDate().toLocaleDateString()
                                        : "Unlimited"}
                                </td>
                                <td>{member.barcode || "N/A"}</td>
                                <td>
                                    <div className="flex items-center justify-center gap-2 flex-wrap">
                                        <button
                                            onClick={() => handleUnarchive(member.id)}
                                            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 flex items-center gap-2"
                                        >
                                            <FaUndo />
                                            Restore
                                        </button>
                                        <button
                                            onClick={() => handleDeleteFromArchive(member.id)}
                                            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 flex items-center gap-2"
                                        >
                                            <FaTrash />
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {/* Pagination Controls */}
                <div className="mt-4 flex justify-center">
                    {Array.from(
                        { length: Math.ceil(archivedMembers.length / membersPerPage) },
                        (_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`mx-1 px-3 py-1 rounded ${
                                    currentPage === i + 1
                                        ? 'bg-blue-500 text-white'
                                        : theme === 'dark'
                                            ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                                            : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                                }`}
                            >
                                {i + 1}
                            </button>
                        )
                    )}
                </div>
            </div>
            <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
                <p>{modalMessage}</p>
            </Modal>
        </LoadingOverlay>

    );

}
